import { Button, Collapse, Modal, Tag } from 'antd';
import {
  ArrowRight,
  AudioLines,
  Bot,
  Check,
  ChevronDown,
  CircleHelp,
  Clapperboard,
  Gauge,
  Image as ImageIcon,
  Layers3,
  Moon,
  Package,
  Play,
  Rocket,
  ShieldCheck,
  Sparkles,
  Sun,
  WandSparkles,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { SparkMark, useSparkTheme } from '@spark/shared-ui';
import type { Schema } from '@spark/api-contract';
import { PublicContentPortal, isPublicContentPath } from './public-content';
import { resolveApplicationURL } from './navigation';

const allSections = [
  { id: 'home', label: '首页' },
  { id: 'product', label: '产品' },
  { id: 'cases', label: '案例' },
  { id: 'pricing', label: '定价' },
  { id: 'help', label: '帮助' },
] as const;

const defaultProducts = [
  {
    icon: ImageIcon,
    eyebrow: 'IMAGE CREATION',
    title: '图片创作',
    description: '支持文生图与图生图，生成角色、场景、商品主图和详情页。',
    accent: 'blue',
  },
  {
    icon: Clapperboard,
    eyebrow: 'VIDEO REPLACEMENT',
    title: '视频元素替换',
    description: '组合参考视频、人物与商品素材，生成高还原度、多规格的商业视频。',
    accent: 'cyan',
  },
  {
    icon: Clapperboard,
    eyebrow: 'VIDEO CREATION',
    title: '视频创作',
    description: '支持全能参考、单图参考与首尾帧，使用台词、音色描述和环境音控制模型原生声音。',
    accent: 'orange',
  },
];

const defaultCases = [
  {
    title: '夏日气泡水',
    meta: '15 秒商品宣传片',
    duration: '00:15',
    tone: 'blue',
    tag: '多帧继承',
  },
  {
    title: '高端护肤礼盒',
    meta: '角色场景宣传片',
    duration: '00:20',
    tone: 'orange',
    tag: '图生视频',
  },
  {
    title: '智能穿戴设备',
    meta: '科技新品发布短片',
    duration: '00:18',
    tone: 'cyan',
    tag: '高清输出',
  },
];

const defaultPlans = [
  {
    name: '入门体验',
    price: '免费体验',
    description: '适合初次体验 AI 创作流程',
    features: ['注册即送体验积分', '基础 AI 应用', '标准任务队列'],
    action: '免费开始',
  },
  {
    name: 'Pro 周期套餐',
    price: '多周期可选',
    description: '适合高频内容创作者与小团队',
    features: ['按周期发放套餐积分', '更高任务并发', '多规格内容导出'],
    action: '查看 Pro 套餐',
    featured: true,
  },
  {
    name: 'Max 周期套餐',
    price: '长期更优惠',
    description: '适合品牌、代理商与规模化生产',
    features: ['更高并发与全规格输出', '代理商充值与兑换券', '专属服务与成本分析'],
    action: '查看 Max 套餐',
  },
];

const defaultFAQItems = [
  {
    key: '1',
    label: '积分如何充值和消耗？',
    children: '平台按 1 元 = 1000 积分充值。每次生成会根据所选模型、分辨率、时长等参数预估积分，任务完成后按实际消耗结算。',
  },
  {
    key: '2',
    label: '生成任务需要一直停留在页面吗？',
    children: '不需要。所有生成任务均为异步执行；同一用户内按提交顺序处理，不同用户之间公平轮询，避免单个用户长期占满算力。结果保留在任务中心，用户可按需保存到素材库。',
  },
  {
    key: '3',
    label: '上传的图片、音频和视频会保存在哪里？',
    children: '用户主动上传或从任务结果保存的内容会进入素材库，按图片、视频、音频和具体用途管理，并支持按名称或简介检索。',
  },
];
const productIcons = [AudioLines, ImageIcon, Clapperboard, WandSparkles];

type WebsiteContent = Schema<'WebsiteContent'>;

function hasActiveConsoleSession() {
  try {
    const raw = window.sessionStorage.getItem('spark_user_session');
    if (!raw) return false;
    const session = JSON.parse(raw) as { token?: string; expiresAt?: string };
    return Boolean(session.token && session.expiresAt && new Date(session.expiresAt).getTime() > Date.now());
  } catch {
    return false;
  }
}

const fallbackContent: WebsiteContent = {
  schemaVersion: 1,
  site: {
    brandName: '麦淼 AI', companyName: '河南创淼科技有限公司', icpRecord: '备案号：待补充', icpUrl: 'https://beian.miit.gov.cn/',
    supportLabel: '联系平台顾问', supportUrl: '#help',
    navigation: allSections.map((section) => ({ section: section.id, label: section.label })),
    footerLinks: [{ label: '用户协议', url: '/agreement/user' }, { label: '隐私政策', url: '/agreement/privacy' }, { label: '充值与套餐说明', url: '/agreement/billing' }, { label: '平台公告', url: '/announcements' }, { label: '帮助中心', url: '/help' }],
  },
  seo: {
    title: '麦淼 AI｜一站式商业内容智能创作平台',
    description: '麦淼 AI 将角色、场景、商品和声音汇入同一条智能创作链路，一站完成商业图片与视频内容生产。',
    keywords: ['麦淼 AI', 'AI 图片创作', 'AI 视频创作', '电商内容生成'],
    shareImageUrl: '',
  },
  hero: {
    enabled: true,
    kicker: 'AI CREATIVE OPERATING SYSTEM',
    titleLine1: '以星火为引，',
    titleLine2: '赴灵感之约。',
    englishSlogan: 'Follow the spark to your inspiration.',
    description: '麦淼 AI 将角色、场景、商品和声音汇入统一创作链路，覆盖图片生成、声音克隆、动作迁移与短视频创作。',
    primaryLabel: '免费开始创作', primaryUrl: '/console/', secondaryLabel: '查看视频案例', secondaryUrl: '#cases', videoUrl: '/video.mp4',
    signals: ['多智能体协同', '素材安全留存', '异步并发队列'],
  },
  product: { enabled: true, eyebrow: '01 / PRODUCT MATRIX', title: '从单点工具，到完整创作链路', description: '所有应用共享同一套素材、积分、任务和生成记录，未来新增 AI 能力也能快速接入。', items: defaultProducts.map(({ eyebrow, title, description, accent }) => ({ eyebrow, title, description, tone: accent as 'blue' | 'cyan' | 'orange' })) },
  cases: { enabled: true, eyebrow: '02 / VIDEO SHOWCASE', title: '让每一次生成，都接近商业成片', description: '从消费品到科技新品，智能体理解产品卖点，并保持人物、场景与镜头语言连续。', items: defaultCases.map((item) => ({ ...item, tone: item.tone as 'blue' | 'cyan' | 'orange' })) },
  pricing: { enabled: true, eyebrow: '03 / PRICING', title: '为不同创作规模，准备合适的方案', description: '充值统一兑换积分，用多少、扣多少；不同模型和输出规格均清晰计价。', note: '正式套餐金额、积分额度与模型单价将以平台后台发布版本为准。', items: defaultPlans.map(({ name, ...item }) => ({ title: name, ...item })) },
  help: { enabled: true, eyebrow: '04 / HELP CENTER', title: '开始之前，你可能想了解这些', description: '从积分规则、异步任务到素材管理，帮助中心会持续更新平台使用指南与公告。', faqs: defaultFAQItems.map((item) => ({ question: item.label, answer: item.children })), cta: { kicker: 'NEED MORE HELP?', title: '还有问题？我们随时提供帮助。', description: '查看完整帮助文档，或联系平台顾问了解团队版、代理商与定制接入方案。', label: '进入帮助中心', url: '#help' } },
};

function HomePage() {
  const { resolved, toggle } = useSparkTheme();
  const currentYear = new Date().getFullYear();
  const mainRef = useRef<HTMLElement>(null);
  const [content, setContent] = useState<WebsiteContent>(fallbackContent);
  const [siteOffline, setSiteOffline] = useState(false);
  const [caseVideo, setCaseVideo] = useState<{ title: string; url: string } | null>(null);
  const [authenticated, setAuthenticated] = useState(hasActiveConsoleSession);
  const labels = new Map((content.site.navigation || []).map((item) => [item.section, item.label]));
  const visibleSections = allSections
    .filter((section) => section.id === 'home' ? content.hero.enabled : content[section.id].enabled)
    .map((section) => ({ ...section, label: labels.get(section.id) || section.label }));
  const headerSections = (content.site.navigation || [])
    .map((item) => visibleSections.find((section) => section.id === item.section))
    .filter((section): section is (typeof visibleSections)[number] => Boolean(section));
  const visibleSectionKey = visibleSections.map((item) => item.id).join(',');
  const firstSection = visibleSections[0]?.id || 'home';
  const sectionAfterHero = visibleSections.find((section) => section.id !== 'home');
  const [activeSection, setActiveSection] = useState<(typeof allSections)[number]['id']>('home');

  useEffect(() => {
    let active = true;
    fetch('/api/v1/public/website-pages/home')
      .then((response) => {
        if (response.status === 404) {
          if (active) setSiteOffline(true);
          return null;
        }
        return response.ok ? response.json() : Promise.reject(new Error('website content unavailable'));
      })
      .then((payload) => {
        if (active && payload?.version?.content?.schemaVersion === 1) {
          const remote = payload.version.content as Partial<WebsiteContent>;
          setContent({ ...fallbackContent, ...remote, site: { ...fallbackContent.site, ...(remote.site || {}) }, seo: { ...fallbackContent.seo, ...(remote.seo || {}) } });
        }
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const syncSession = () => {
      if (hasActiveConsoleSession()) setAuthenticated(true);
      fetch('/api/v1/public/session', { credentials: 'same-origin', cache: 'no-store' })
        .then((response) => response.ok ? response.json() : null)
        .then((payload: { authenticated?: boolean } | null) => {
          if (active && typeof payload?.authenticated === 'boolean') setAuthenticated(payload.authenticated);
        })
        .catch(() => undefined);
    };
    syncSession();
    window.addEventListener('focus', syncSession);
    window.addEventListener('storage', syncSession);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncSession();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      active = false;
      window.removeEventListener('focus', syncSession);
      window.removeEventListener('storage', syncSession);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    document.title = content.seo.title;
    const setMeta = (selector: string, attribute: 'name' | 'property', key: string, value: string) => {
      let node = document.head.querySelector<HTMLMetaElement>(selector);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      node.content = value;
    };
    setMeta('meta[name="description"]', 'name', 'description', content.seo.description);
    setMeta('meta[name="keywords"]', 'name', 'keywords', (content.seo.keywords || []).join(','));
    setMeta('meta[property="og:title"]', 'property', 'og:title', content.seo.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', content.seo.description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', content.site.brandName);
    setMeta('meta[property="og:image"]', 'property', 'og:image', content.seo.shareImageUrl ? new URL(content.seo.shareImageUrl, window.location.origin).href : '');
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', content.seo.shareImageUrl ? 'summary_large_image' : 'summary');
  }, [content.seo, content.site.brandName]);

  useEffect(() => {
    if (!visibleSections.some((section) => section.id === activeSection)) {
      setActiveSection(firstSection);
    }
  }, [activeSection, firstSection, visibleSectionKey]);

  useEffect(() => {
    const container = mainRef.current;
    if (!container) return;

    const nodes = Array.from(container.querySelectorAll<HTMLElement>('[data-snap-section]'));
    if (nodes.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (mostVisible?.target.id) {
          setActiveSection(mostVisible.target.id as (typeof allSections)[number]['id']);
        }
      },
      { root: container, threshold: [0.35, 0.55, 0.75] },
    );

    nodes.forEach((node) => observer.observe(node));

    let wheelLocked = false;
    let unlockTimer: number | undefined;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 12 || wheelLocked) return;

      const currentIndex = nodes.reduce((bestIndex, node, index) => {
        const currentDistance = Math.abs(nodes[bestIndex].offsetTop - container.scrollTop);
        const nextDistance = Math.abs(node.offsetTop - container.scrollTop);
        return nextDistance < currentDistance ? index : bestIndex;
      }, 0);
      const targetIndex = Math.max(0, Math.min(nodes.length - 1, currentIndex + (event.deltaY > 0 ? 1 : -1)));
      if (targetIndex === currentIndex) return;

      event.preventDefault();
      wheelLocked = true;
      nodes[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      unlockTimer = window.setTimeout(() => {
        wheelLocked = false;
      }, 760);
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      observer.disconnect();
      container.removeEventListener('wheel', onWheel);
      if (unlockTimer) window.clearTimeout(unlockTimer);
    };
  }, [visibleSectionKey]);

  const navigateSection = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openURL = (target?: string) => {
    if (!target) return;
    if (target.startsWith('#')) document.getElementById(target.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.location.assign(resolveApplicationURL(target));
  };

  const moveNova = (event: PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    event.currentTarget.style.setProperty('--nova-x', `${x * 14}px`);
    event.currentTarget.style.setProperty('--nova-y', `${y * 12}px`);
    event.currentTarget.style.setProperty('--nova-rx', `${y * -2.4}deg`);
    event.currentTarget.style.setProperty('--nova-ry', `${x * 2.8}deg`);
  };

  const resetNova = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.style.setProperty('--nova-x', '0px');
    event.currentTarget.style.setProperty('--nova-y', '0px');
    event.currentTarget.style.setProperty('--nova-rx', '0deg');
    event.currentTarget.style.setProperty('--nova-ry', '0deg');
  };

  if (siteOffline) {
    return <div className="web-site web-site-offline"><div className="web-offline-card"><SparkMark /><span>WEBSITE OFFLINE</span><h1>官网内容正在维护</h1><p>运营团队正在更新页面内容，请稍后再来。</p><Button className="web-primary-button" type="primary" onClick={() => window.location.reload()}>重新加载</Button></div></div>;
  }

  return (
    <div className="web-site">
      <div className="web-background-stars" aria-hidden="true">
        {Array.from({ length: 15 }, (_, index) => <i key={index} />)}
      </div>
      <header className="web-nav">
        <a className="web-brand-link" href={`#${firstSection}`} onClick={(event) => navigateSection(event, firstSection)}>
          <SparkMark />
        </a>

        <nav className="web-nav-links" aria-label="官网导航">
          {headerSections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={activeSection === section.id ? 'is-active' : ''}
              aria-current={activeSection === section.id ? 'page' : undefined}
              onClick={(event) => navigateSection(event, section.id)}
            >
              {section.label}
            </a>
          ))}
        </nav>

        <div className="web-nav-actions">
          <button className="web-theme-toggle" type="button" aria-label="切换明暗主题" onClick={toggle}>
            {resolved === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Button className="web-primary-button" type="primary" onClick={() => openURL('/console/')}>
            {authenticated ? '进入控制台' : '登录 / 注册'} <ArrowRight size={16} />
          </Button>
        </div>
      </header>

      <main ref={mainRef} className="web-snap-main">
        {content.hero.enabled && <section
          id="home"
          data-snap-section
          className={`web-snap-section web-home-section ${activeSection === 'home' ? 'is-visible' : ''}`}
        >
          <div className="web-orbit web-orbit-one" />
          <div className="web-orbit web-orbit-two" />
          <div className="web-section-inner web-hero-layout">
            <div className="web-hero-copy">
              <div className="web-kicker"><Sparkles size={15} /> {content.hero.kicker}</div>
              <h1>
                <span className="web-hero-line web-hero-line-main">{content.hero.titleLine1}</span>
                <span className="web-hero-line web-hero-line-accent">{content.hero.titleLine2}</span>
              </h1>
              <p className="web-hero-slogan-en" lang="en">
                <em>{content.hero.englishSlogan}</em>
              </p>
              <p className="web-hero-description">{content.hero.description}</p>
              <div className="web-hero-actions">
                <Button className="web-primary-button web-hero-primary" type="primary" size="large" onClick={() => openURL(content.hero.primaryUrl)}>
                  {content.hero.primaryLabel} <Rocket size={18} />
                </Button>
                <a className="web-text-link" href={content.hero.secondaryUrl} onClick={(event) => { if (content.hero.secondaryUrl.startsWith('#')) navigateSection(event, content.hero.secondaryUrl.slice(1)); }}>
                  <span className="web-play-mini"><Play size={13} fill="currentColor" /></span>
                  {content.hero.secondaryLabel}
                </a>
              </div>
              <div className="web-hero-signals web-stagger">
                {(content.hero.signals || []).map((signal, index) => <span key={signal}>{index === 0 ? <Zap size={15} /> : index === 1 ? <ShieldCheck size={15} /> : <Gauge size={15} />} {signal}</span>)}
              </div>
            </div>

            <div
              className="web-hero-video-stage"
              aria-label="麦淼 AI 视频案例自动播放"
              onPointerMove={moveNova}
              onPointerLeave={resetNova}
            >
              <div className="web-video-planet-orbits" aria-hidden="true">
                <div className="web-video-energy-orbit web-video-energy-orbit-one"><i /></div>
                <div className="web-video-energy-orbit web-video-energy-orbit-two"><i /></div>
                <div className="web-video-energy-orbit web-video-energy-orbit-three"><i /></div>
              </div>
              <div className="web-video-energy-frame">
                <svg className="web-video-border-comet" viewBox="0 0 1000 562" preserveAspectRatio="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="video-route-spectrum" x1="0" y1="0" x2="1" y2="0">
                      <stop stopColor="#146cff" stopOpacity=".08" />
                      <stop offset=".5" stopColor="#46ddff" stopOpacity=".78" />
                      <stop offset=".82" stopColor="#f4ffff" stopOpacity=".98" />
                      <stop offset="1" stopColor="#ff8120" stopOpacity=".48" />
                    </linearGradient>
                    <linearGradient id="video-comet-tail-cyan" x1="0" x2="1">
                      <stop stopColor="#1674ff" stopOpacity="0" />
                      <stop offset=".52" stopColor="#35dfff" stopOpacity=".42" />
                      <stop offset="1" stopColor="#efffff" stopOpacity=".96" />
                    </linearGradient>
                    <linearGradient id="video-comet-tail-fire" x1="0" x2="1">
                      <stop stopColor="#ff4d00" stopOpacity="0" />
                      <stop offset=".58" stopColor="#ff6810" stopOpacity=".7" />
                      <stop offset=".82" stopColor="#ff9d2d" stopOpacity=".9" />
                      <stop offset="1" stopColor="#ffdb82" stopOpacity=".95" />
                    </linearGradient>
                    <radialGradient id="video-comet-head">
                      <stop stopColor="#ffffff" />
                      <stop offset=".36" stopColor="#d9fdff" />
                      <stop offset=".7" stopColor="#4de3ff" />
                      <stop offset="1" stopColor="#187cff" stopOpacity=".2" />
                    </radialGradient>
                    <filter id="video-comet-glow" x="-120%" y="-160%" width="340%" height="420%">
                      <feGaussianBlur stdDeviation="6" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <filter id="video-route-glow" x="-30%" y="-60%" width="160%" height="220%">
                      <feGaussianBlur stdDeviation="4" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>
                  <rect
                    className="web-video-comet-route"
                    x="7"
                    y="7"
                    width="986"
                    height="548"
                    rx="42"
                    pathLength="100"
                    filter="url(#video-route-glow)"
                  >
                    <animate attributeName="stroke-dashoffset" from="18" to="-82" dur="16s" repeatCount="indefinite" />
                  </rect>
                  <g className="web-video-comet-head" filter="url(#video-comet-glow)">
                    <path className="web-video-comet-tail-cyan" d="M2 0C-18-13-48-11-82 0C-48 11-18 13 2 0Z" />
                    <path className="web-video-comet-tail-fire" d="M-2 5C-19 3-37 9-60 19C-34 15-15 14 4 4Z" />
                    <circle className="web-video-comet-halo" cx="0" cy="0" r="18" />
                    <circle className="web-video-comet-core" cx="0" cy="0" r="8" />
                    <circle className="web-video-comet-white" cx="2" cy="-2" r="3" />
                    <animateMotion
                      path="M49 7H951A42 42 0 0 1 993 49V513A42 42 0 0 1 951 555H49A42 42 0 0 1 7 513V49A42 42 0 0 1 49 7Z"
                      dur="16s"
                      repeatCount="indefinite"
                      rotate="auto"
                    />
                  </g>
                </svg>
                <div className="web-video-viewport">
                  <video autoPlay muted loop playsInline preload="metadata">
                    <source src={content.hero.videoUrl} type="video/mp4" />
                  </video>
                  <div className="web-video-color-wash" />
                  <div className="web-video-edge-vignette" />
                  <div className="web-video-scan-light" />
                  <span className="web-video-showreel-label"><Sparkles size={12} /> AI SHOWREEL</span>
                  <span className="web-video-live-indicator"><i /> GENERATING VISUAL</span>
                </div>
              </div>

              <div className="web-video-sparks" aria-hidden="true">
                {Array.from({ length: 14 }, (_, index) => <i key={index} />)}
              </div>

              <svg className="web-nova-svg" viewBox="0 0 720 600" role="img" aria-hidden="true">
                <defs>
                  <radialGradient id="nova-field-blue">
                    <stop offset="0" stopColor="#68e8ff" stopOpacity=".42" />
                    <stop offset=".38" stopColor="#1684ff" stopOpacity=".22" />
                    <stop offset="1" stopColor="#1684ff" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient id="nova-field-fire">
                    <stop offset="0" stopColor="#ff8a1f" stopOpacity=".36" />
                    <stop offset="1" stopColor="#ff6a0a" stopOpacity="0" />
                  </radialGradient>
                  <linearGradient id="nova-blue-stroke" x1="0" x2="1">
                    <stop stopColor="#176bff" stopOpacity="0" />
                    <stop offset=".35" stopColor="#36cfff" />
                    <stop offset=".72" stopColor="#0878ff" />
                    <stop offset="1" stopColor="#5ee8ff" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="nova-fire-stroke" x1="0" x2="1">
                    <stop stopColor="#ffca62" stopOpacity="0" />
                    <stop offset=".32" stopColor="#ffad31" />
                    <stop offset=".7" stopColor="#ff5d0a" />
                    <stop offset="1" stopColor="#ff5d0a" stopOpacity="0" />
                  </linearGradient>
                  <radialGradient id="nova-star-core">
                    <stop stopColor="#ffffff" />
                    <stop offset=".34" stopColor="#bdF7ff" />
                    <stop offset=".66" stopColor="#32d7ff" />
                    <stop offset="1" stopColor="#176bff" />
                  </radialGradient>
                  <filter id="nova-blue-glow" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur stdDeviation="8" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="nova-fire-glow" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>

                <g className="web-nova-depth">
                  <ellipse cx="366" cy="301" rx="330" ry="250" fill="url(#nova-field-blue)" />
                  <ellipse cx="470" cy="360" rx="205" ry="180" fill="url(#nova-field-fire)" />
                  <path className="web-nova-grid-line" d="M75 395C218 286 500 216 684 274" />
                  <path className="web-nova-grid-line web-nova-grid-line-two" d="M88 451C278 340 503 328 664 375" />
                  <ellipse className="web-nova-scan-ring" cx="360" cy="300" rx="263" ry="178" />
                  <ellipse className="web-nova-scan-ring web-nova-scan-ring-two" cx="360" cy="300" rx="188" ry="128" />
                </g>

                <g className="web-nova-blue-arcs" fill="none">
                  <path id="nova-arc-a" d="M66 338C174 126 520 106 672 326" />
                  <path id="nova-arc-b" d="M96 432C278 515 577 460 663 216" />
                  <path id="nova-arc-c" d="M168 132C355 44 586 156 604 397" />
                  <circle className="web-nova-traveller web-nova-traveller-a" r="5">
                    <animateMotion dur="6.5s" repeatCount="indefinite"><mpath href="#nova-arc-a" /></animateMotion>
                  </circle>
                  <circle className="web-nova-traveller web-nova-traveller-b" r="4">
                    <animateMotion dur="8s" begin="-3s" repeatCount="indefinite"><mpath href="#nova-arc-b" /></animateMotion>
                  </circle>
                  <circle className="web-nova-traveller web-nova-traveller-c" r="3.5">
                    <animateMotion dur="7.2s" begin="-1.5s" repeatCount="indefinite"><mpath href="#nova-arc-c" /></animateMotion>
                  </circle>
                </g>

                <g className="web-nova-fire-ribbons" fill="none" filter="url(#nova-fire-glow)">
                  <path d="M348 304C421 245 504 220 636 250C544 257 478 286 414 332C505 306 584 322 686 402" />
                  <path d="M351 313C430 329 489 382 565 489C492 416 430 382 372 370" />
                  <path d="M365 289C416 211 478 174 572 163C490 202 442 252 405 304" />
                </g>

                <g className="web-nova-star-system">
                  <circle className="web-nova-shockwave web-nova-shockwave-one" cx="354" cy="310" r="73" />
                  <circle className="web-nova-shockwave web-nova-shockwave-two" cx="354" cy="310" r="73" />
                  <path className="web-nova-flame-core" d="M354 386C317 367 306 337 324 305C329 327 344 336 353 320C368 293 358 272 379 246C378 275 408 291 411 325C415 362 386 387 354 386Z" />
                  <polygon
                    className="web-nova-star-core"
                    points="354,220 368,286 419,248 379,298 448,310 379,322 419,372 368,334 354,400 340,334 289,372 329,322 260,310 329,298 289,248 340,286"
                  />
                  <circle className="web-nova-white-core" cx="354" cy="310" r="18" />
                  <path className="web-nova-core-cut" d="M354 271L363 301L393 310L363 319L354 349L345 319L315 310L345 301Z" />
                </g>

                <g className="web-nova-particles">
                  {[
                    [112, 270, 4], [152, 170, 3], [207, 486, 3], [264, 116, 4], [474, 98, 3],
                    [592, 136, 5], [638, 474, 3], [570, 520, 4], [106, 410, 2], [668, 310, 3],
                    [515, 264, 2], [450, 448, 3], [218, 238, 2], [610, 358, 2],
                  ].map(([cx, cy, r], index) => <circle key={index} cx={cx} cy={cy} r={r} />)}
                </g>
              </svg>
              <div className="web-nova-flash web-nova-flash-one" />
              <div className="web-nova-flash web-nova-flash-two" />
            </div>
          </div>
          {sectionAfterHero && <a className="web-scroll-cue" href={`#${sectionAfterHero.id}`} onClick={(event) => navigateSection(event, sectionAfterHero.id)}>
            向下探索 <ChevronDown size={16} />
          </a>}
        </section>}

        {content.product.enabled && <section
          id="product"
          data-snap-section
          className={`web-snap-section web-product-section ${activeSection === 'product' ? 'is-visible' : ''}`}
        >
          <div className="web-section-inner">
            <div className="web-section-heading">
              <div>
                <span className="web-section-index">{content.product.eyebrow}</span>
                <h2>{content.product.title}</h2>
              </div>
              <p>{content.product.description}</p>
            </div>

            <div className="web-product-grid web-stagger">
              {(content.product.items || []).map((product, index) => {
                const Icon = productIcons[index % productIcons.length];
                return (
                <article key={product.title} className={`web-product-card web-accent-${product.tone || 'blue'}`}>
                  <div className="web-product-card-top">
                    <span className="web-product-icon"><Icon size={23} /></span>
                    <ArrowRight className="web-card-arrow" size={18} />
                  </div>
                  <small>{product.eyebrow}</small>
                  <h3>{product.title}</h3>
                  <p>{product.description}</p>
                  <span className="web-product-status"><i /> 已接入统一工作台</span>
                </article>
              );})}
            </div>

            <div className="web-flow-strip web-stagger">
              <div><span>01</span><Package size={19} /><p><strong>统一素材</strong><small>分类检索与复用</small></p></div>
              <i />
              <div><span>02</span><Bot size={19} /><p><strong>模型创作</strong><small>图片、声音与视频</small></p></div>
              <i />
              <div><span>03</span><Zap size={19} /><p><strong>异步生成</strong><small>并发队列与进度</small></p></div>
              <i />
              <div><span>04</span><Layers3 size={19} /><p><strong>素材沉淀</strong><small>结果归档与再创作</small></p></div>
            </div>
          </div>
        </section>}

        {content.cases.enabled && <section
          id="cases"
          data-snap-section
          className={`web-snap-section web-cases-section ${activeSection === 'cases' ? 'is-visible' : ''}`}
        >
          <div className="web-section-inner">
            <div className="web-section-heading">
              <div>
                <span className="web-section-index">{content.cases.eyebrow}</span>
                <h2>{content.cases.title}</h2>
              </div>
              <p>{content.cases.description}</p>
            </div>

            <div className="web-case-grid web-stagger">
              {(content.cases.items || []).map((item, index) => (
                <article key={item.title} className="web-case-card">
                  <div className={`web-case-media web-case-${item.tone || 'blue'}`}>
                    <div className="web-case-gridline" />
                    <span className="web-case-number">0{index + 1}</span>
                    <div className="web-case-object"><i /><b>SPARK</b></div>
                    <button type="button" disabled={!item.mediaUrl} aria-label={item.mediaUrl ? `播放${item.title}案例` : `${item.title}暂未配置视频`} onClick={() => item.mediaUrl && setCaseVideo({ title: item.title, url: item.mediaUrl })}><Play size={20} fill="currentColor" /></button>
                    <span className="web-case-duration">{item.duration}</span>
                  </div>
                  <div className="web-case-info">
                    <div><h3>{item.title}</h3><p>{item.meta}</p></div>
                    <Tag>{item.tag}</Tag>
                  </div>
                </article>
              ))}
            </div>

            <div className="web-case-footer">
              <span><Sparkles size={16} /> 全部案例由麦淼 AI 工作流生成</span>
              {content.product.enabled && <a href="#product" onClick={(event) => navigateSection(event, 'product')}>了解生成流程 <ArrowRight size={15} /></a>}
            </div>
          </div>
        </section>}

        {content.pricing.enabled && <section
          id="pricing"
          data-snap-section
          className={`web-snap-section web-pricing-section ${activeSection === 'pricing' ? 'is-visible' : ''}`}
        >
          <div className="web-section-inner">
            <div className="web-section-heading web-heading-centered">
              <div>
                <span className="web-section-index">{content.pricing.eyebrow}</span>
                <h2>{content.pricing.title}</h2>
              </div>
              <p>{content.pricing.description}</p>
            </div>

            <div className="web-plan-grid web-stagger">
              {(content.pricing.items || []).map((plan) => (
                <article key={plan.title} className={`web-plan-card ${plan.featured ? 'is-featured' : ''}`}>
                  {plan.featured && <span className="web-plan-badge"><Zap size={12} /> 推荐方案</span>}
                  <span className="web-plan-name">{plan.title}</span>
                  <strong className="web-plan-price">{plan.price}</strong>
                  <p>{plan.description}</p>
                  <ul>
                    {(plan.features || []).map((feature) => <li key={feature}><Check size={15} /> {feature}</li>)}
                  </ul>
                  <Button className={plan.featured ? 'web-primary-button' : 'web-plan-button'} type={plan.featured ? 'primary' : 'default'} onClick={() => openURL(plan.actionUrl)}>
                    {plan.action} <ArrowRight size={15} />
                  </Button>
                </article>
              ))}
            </div>
            <p className="web-pricing-note">{content.pricing.note}</p>
          </div>
        </section>}

        {content.help.enabled && <section
          id="help"
          data-snap-section
          className={`web-snap-section web-help-section ${activeSection === 'help' ? 'is-visible' : ''}`}
        >
          <div className="web-section-inner web-help-inner">
            <div className="web-section-heading">
              <div>
                <span className="web-section-index">{content.help.eyebrow}</span>
                <h2>{content.help.title}</h2>
              </div>
              <p>{content.help.description}</p>
            </div>

            <div className="web-help-layout web-stagger">
              <div className="web-faq-panel">
                <Collapse ghost accordion items={(content.help.faqs || []).map((item, index) => ({ key: String(index + 1), label: item.question, children: item.answer }))} expandIconPlacement="end" />
              </div>
              <aside className="web-support-card">
                <span className="web-support-icon"><CircleHelp size={24} /></span>
                <span className="web-support-kicker">{content.help.cta?.kicker}</span>
                <h3>{content.help.cta?.title}</h3>
                <p>{content.help.cta?.description}</p>
                <Button className="web-primary-button" type="primary" onClick={() => openURL(content.site.supportUrl || content.help.cta?.url)}>{content.site.supportLabel || content.help.cta?.label} <ArrowRight size={16} /></Button>
              </aside>
            </div>

            <div className="web-final-cta">
              <div><Sparkles size={18} /><p><strong>现在，让创意真正开始流动。</strong><small>注册即可获得体验积分，无需安装任何软件。</small></p></div>
              <Button className="web-primary-button" type="primary" onClick={() => openURL('/console/')}>免费进入麦淼 AI <Rocket size={16} /></Button>
            </div>

            <footer className="web-footer">
              <div className="web-footer__brand">
                <SparkMark compact />
                <div className="web-footer__identity">
                  <strong>© {currentYear} {content.site.companyName} 版权所有 · {content.site.brandName}</strong>
                  {content.site.icpRecord && <a className="web-footer__icp" href={content.site.icpUrl} target="_blank" rel="noreferrer">{content.site.icpRecord}</a>}
                </div>
              </div>
              <nav aria-label="网站政策与服务">
                {(content.site.footerLinks || []).map((item) => <a key={`${item.label}-${item.url}`} href={item.url} onClick={(event) => { if (item.url.startsWith('#')) navigateSection(event, item.url.slice(1)); }}>{item.label}</a>)}
              </nav>
            </footer>
          </div>
        </section>}
      </main>

      <nav className="web-section-dots" aria-label="章节快速导航">
        {visibleSections.map((section, index) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            aria-label={section.label}
            className={activeSection === section.id ? 'is-active' : ''}
            onClick={(event) => navigateSection(event, section.id)}
          >
            <span>0{index + 1}</span><i />
          </a>
        ))}
      </nav>
      <Modal title={caseVideo?.title} open={Boolean(caseVideo)} footer={null} width={960} destroyOnHidden onCancel={() => setCaseVideo(null)}>
        {caseVideo && <video controls autoPlay playsInline style={{ display: 'block', width: '100%', borderRadius: '1rem', background: '#000' }} src={caseVideo.url} />}
      </Modal>
    </div>
  );
}

export function App() {
  return isPublicContentPath(window.location.pathname) ? <PublicContentPortal /> : <HomePage />;
}
